import { useCallback, useMemo } from "react";
import { ArchiveIcon, ArrowLeft } from "lucide-react";
import { MarketingWorkFile } from "@/bryntum-lib/models/MarketingWorkFile";
import { MarketingWorkFileCard } from "@/bryntum-lib/models/MarketingWorkFileCard";
import type { MarketingDocumentFormat } from "@/bryntum-lib/models/MarketingDocument";
import { cn } from "@/modern/utils";
import { useWorkFileDetail } from "../hooks/useWorkFiles";
import { useMarketingDocumentGenerate } from "../hooks/useMarketingDocumentGenerate";
import { useMarketingPageGenerate } from "@/react/components/entityViews/marketingPages/hooks/useMarketingPageGenerate";
import { WorkFileCardList } from "./WorkFileCardList";
import { WorkFileLivePreview } from "./WorkFileLivePreview";
import { PublishMarketingPageAction } from "./PublishMarketingPageAction";
import { GenerateMarketingDocumentAction } from "./GenerateMarketingDocumentAction";
import { WorkFileOutputsPanel } from "./WorkFileOutputsPanel";

interface WorkFileDetailViewProps {
  file: MarketingWorkFile;
  cards: MarketingWorkFileCard[];
  onBack: () => void;
}

export const WorkFileDetailView = ({ file, cards, onBack }: WorkFileDetailViewProps) => {
  const {
    fileCards,
    linkedPages,
    linkedDocuments,
    selectedCardId,
    selectedCard,
    handleSelectCard,
    isAddingCard,
    handleAddCard,
    handleCancelAdd,
    handleSave,
    handleDelete,
    handleReorderCards,
  } = useWorkFileDetail(file, cards);

  const {
    generating: documentGenerating,
    pending: documentPending,
    clearPending,
    startGenerateForWorkFile,
  } = useMarketingDocumentGenerate();

  const {
    generating: pageGenerating,
    isTestDrive: pageGenerateTestDriveRestricted,
    startGenerateForWorkFile: startPublishPage,
  } = useMarketingPageGenerate();

  const knownDocumentIds = useMemo(
    () => linkedDocuments.map((d) => Number(d.id)),
    [linkedDocuments],
  );

  const handleGenerateDocument = useCallback(
    (format: MarketingDocumentFormat, name: string) =>
      startGenerateForWorkFile(Number(file.id), format, name, knownDocumentIds),
    [file.id, knownDocumentIds, startGenerateForWorkFile],
  );

  const handlePublishPage = useCallback(() => {
    startPublishPage(Number(file.id));
  }, [file.id, startPublishPage]);

  const handleDocumentAppeared = useCallback(() => {
    clearPending();
  }, [clearPending]);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border bg-accent px-4 py-2">
        <div className="flex min-w-0 items-center gap-2">
          {onBack && (
            <button
              type="button"
              aria-label="Back to work files"
              onClick={onBack}
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
            </button>
          )}
          <div className="flex min-w-0 items-center gap-1.5">
            <span
              className={cn(
                "truncate text-sm font-medium",
                file.archived ? "text-muted-foreground line-through" : "text-foreground",
              )}
            >
              {file.displayName}
            </span>
            {file.archived && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-500">
                <ArchiveIcon className="h-2.5 w-2.5" />
                Archived
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <GenerateMarketingDocumentAction
            file={file}
            generating={documentGenerating}
            onGenerate={handleGenerateDocument}
          />
          <PublishMarketingPageAction
            file={file}
            generating={pageGenerating}
            onPublish={handlePublishPage}
            testDriveRestricted={pageGenerateTestDriveRestricted}
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden p-4">
        <div className="grid h-full grid-cols-1 gap-4 lg:grid-cols-[300px_1fr_300px]">
          <WorkFileCardList
            cards={fileCards}
            selectedCardId={selectedCardId}
            onSelectCard={handleSelectCard}
            onAddCard={handleAddCard}
            isAddingCard={isAddingCard}
            onReorderCards={handleReorderCards}
          />
          <WorkFileLivePreview
            card={selectedCard}
            isNewCard={isAddingCard}
            onSave={handleSave}
            onDelete={handleDelete}
            onCancel={handleCancelAdd}
          />
          <div className="hidden lg:flex overflow-hidden rounded-xl border border-border flex-col">
            <WorkFileOutputsPanel
              pages={linkedPages}
              documents={linkedDocuments}
              workFile={file}
              documentPending={documentPending}
              onDocumentAppeared={handleDocumentAppeared}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkFileDetailView;
